import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Search, X, Eye, Plus, Trash2, Edit, SlidersHorizontal, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${m}/${day}/${y}`;
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  const s = String(d);
  return `${fmtDate(s)} ${s.slice(11, 16)}`;
};
const money = (v) => `$${(v || 0).toFixed(2)}`;

const STATUS_STYLES = {
  finalized: 'bg-green-100 text-green-700',
  completed: 'bg-green-100 text-green-700',
  preview: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
};

const PayrollReportsList = ({
  reports = [],
  language = 'pt-BR',
  onView,
  onGeneratePDFs,
  onAddRemoveClients,
  onEditReports,
  onBackcharges,
  onClearThrottle,
  onExportCSV,
}) => {
  const pt = language === 'pt-BR';
  const [search, setSearch] = useState('');
  const [year, setYear] = useState('all');
  const [month, setMonth] = useState('all');
  const [team, setTeam] = useState('all');
  const [sortBy, setSortBy] = useState('week_desc');
  const [showFilters, setShowFilters] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const MONTHS = pt
    ? ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const years = useMemo(() => {
    const s = new Set();
    reports.forEach((r) => r.week_ending && s.add(String(r.week_ending).slice(0, 4)));
    return [...s].sort().reverse();
  }, [reports]);

  const teams = useMemo(() => {
    const s = new Set();
    reports.forEach((r) => (r.entries || []).forEach((e) => e.team_name && s.add(e.team_name)));
    return [...s].sort((a, b) => {
      const ma = /^PM(\d+)$/.exec(a); const mb = /^PM(\d+)$/.exec(b);
      if (ma && mb) return parseInt(ma[1], 10) - parseInt(mb[1], 10);
      return a.localeCompare(b);
    });
  }, [reports]);

  const filtered = useMemo(() => {
    const q = norm(search.trim());
    let list = reports.map((r) => {
      const entries = r.entries || [];
      const laborTotal = entries.reduce((s, e) => s + (e.labor_total || 0), 0);
      const teamsInReport = [...new Set(entries.map((e) => e.team_name).filter(Boolean))];
      let matches = [];
      if (q) {
        matches = entries.filter((e) =>
          norm(e.customer).includes(q) || norm(e.job_number).includes(q) || norm(e.team_name).includes(q)
        );
      }
      return { ...r, _laborTotal: laborTotal, _teams: teamsInReport, _matches: matches };
    });

    if (year !== 'all') list = list.filter((r) => String(r.week_ending || '').startsWith(year));
    if (month !== 'all') list = list.filter((r) => String(r.week_ending || '').slice(5, 7) === month);
    if (team !== 'all') list = list.filter((r) => r._teams.includes(team));
    if (q) {
      list = list.filter((r) => r._matches.length > 0 || norm(fmtDate(r.week_ending)).includes(q) || norm(r.week_ending).includes(q));
    }

    const cmp = {
      week_desc: (a, b) => String(b.week_ending || '').localeCompare(String(a.week_ending || '')),
      week_asc: (a, b) => String(a.week_ending || '').localeCompare(String(b.week_ending || '')),
      entries_desc: (a, b) => (b.entries?.length || 0) - (a.entries?.length || 0),
      entries_asc: (a, b) => (a.entries?.length || 0) - (b.entries?.length || 0),
      labor_desc: (a, b) => b._laborTotal - a._laborTotal,
      labor_asc: (a, b) => a._laborTotal - b._laborTotal,
      created_desc: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')),
    }[sortBy];
    if (cmp) list.sort(cmp);
    return list;
  }, [reports, search, year, month, team, sortBy]);

  // Agrupar por mês/ano (grupos ordenados do mais recente ao mais antigo)
  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((r) => {
      const key = String(r.week_ending || '').slice(0, 7) || 'sem-data';
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.keys(map).sort().reverse().map((key) => {
      const [y, m] = key.split('-');
      const label = m ? `${MONTHS[parseInt(m, 10) - 1]} ${y}` : (pt ? 'Sem data' : 'No date');
      const monthTotal = map[key].reduce((s, r) => s + r._laborTotal, 0);
      return { key, label, items: map[key], monthTotal };
    });
  }, [filtered, MONTHS, pt]);

  const activeFilterCount = [year !== 'all', month !== 'all', team !== 'all', sortBy !== 'week_desc'].filter(Boolean).length;
  const hasFilters = search.trim() || activeFilterCount > 0;
  const resetFilters = () => { setSearch(''); setYear('all'); setMonth('all'); setTeam('all'); setSortBy('week_desc'); };
  const toggleGroup = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const selCls = 'border border-slate-200 rounded px-1.5 py-1 text-[11px] bg-white text-slate-600 focus:outline-none';

  return (
    <div className="space-y-3" data-testid="payroll-reports-list">
      {/* Header + ações */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h4 className="font-semibold text-sm sm:text-base">
          📋 {pt ? 'Histórico de Relatórios' : 'Reports History'}
          <span className="ml-2 text-xs font-normal text-slate-400">
            {filtered.length}{filtered.length !== reports.length ? ` ${pt ? 'de' : 'of'} ${reports.length}` : ''}
          </span>
        </h4>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={onAddRemoveClients} variant="outline" size="sm" className="text-green-600 hover:text-green-700 border-green-300 text-xs px-2 py-1" title={pt ? 'Adicionar/Remover Clientes' : 'Add/Remove Clients'} data-testid="payroll-add-remove-clients-btn">
            <Plus className="h-3 w-3 mr-1" /><Trash2 className="h-3 w-3" />
          </Button>
          <Button onClick={onEditReports} variant="outline" size="sm" className="text-blue-600 hover:text-blue-700 border-blue-300 text-xs px-2 py-1" title={pt ? 'Editar Relatórios' : 'Edit Reports'} data-testid="payroll-edit-reports-btn">
            <Edit className="h-3 w-3" />
          </Button>
          <Button onClick={onBackcharges} variant="outline" size="sm" className="text-yellow-700 hover:text-yellow-800 border-yellow-300 text-xs px-2 py-1" title={pt ? 'Ajustes: Backcharge / Adicional' : 'Adjustments: Backcharge / Additional'} data-testid="payroll-backcharges-btn">
            <AlertTriangle className="h-3 w-3 mr-1" />{pt ? 'Ajustes' : 'Adjustments'}
          </Button>
          <Button onClick={onClearThrottle} variant="outline" size="sm" className="text-xs px-2 py-1" title={pt ? 'Limpar throttling de PDF' : 'Clear PDF throttling'} data-testid="payroll-clear-throttle-btn">🔧</Button>
          <Button onClick={onExportCSV} variant="outline" size="sm" className="text-xs px-2 py-1" title="Exportar CSV" data-testid="payroll-export-csv-btn">📊</Button>
        </div>
      </div>

      {/* Busca + filtro compactos (uma linha) */}
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={pt ? 'Buscar cliente, job ou equipe...' : 'Search customer, job or team...'}
            className="w-full h-8 pl-7 pr-7 text-xs border border-slate-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-300"
            data-testid="payroll-reports-search-input"
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="payroll-reports-search-clear">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className={`relative h-8 px-2 rounded-md border text-slate-500 hover:text-slate-700 flex items-center gap-1 ${showFilters || activeFilterCount ? 'border-blue-300 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white'}`}
          data-testid="payroll-filters-toggle"
          title={pt ? 'Filtros e ordenação' : 'Filters & sort'}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {activeFilterCount > 0 && (
            <span className="text-[10px] font-bold">{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Filtros expandíveis (sutis) */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="payroll-filters-row">
          <select value={year} onChange={(e) => setYear(e.target.value)} className={selCls} data-testid="payroll-filter-year">
            <option value="all">{pt ? 'Ano' : 'Year'}</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(e.target.value)} className={selCls} data-testid="payroll-filter-month">
            <option value="all">{pt ? 'Mês' : 'Month'}</option>
            {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
          </select>
          <select value={team} onChange={(e) => setTeam(e.target.value)} className={selCls} data-testid="payroll-filter-team">
            <option value="all">{pt ? 'Equipe' : 'Team'}</option>
            {teams.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selCls} data-testid="payroll-sort-select">
            <option value="week_desc">{pt ? 'Recente' : 'Newest'}</option>
            <option value="week_asc">{pt ? 'Antiga' : 'Oldest'}</option>
            <option value="labor_desc">{pt ? 'Valor ↓' : 'Amount ↓'}</option>
            <option value="labor_asc">{pt ? 'Valor ↑' : 'Amount ↑'}</option>
            <option value="entries_desc">{pt ? 'Entradas ↓' : 'Entries ↓'}</option>
            <option value="entries_asc">{pt ? 'Entradas ↑' : 'Entries ↑'}</option>
            <option value="created_desc">{pt ? 'Criado ↓' : 'Created ↓'}</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={resetFilters} className="text-[11px] text-red-500 hover:text-red-600 flex items-center gap-0.5 px-1" data-testid="payroll-clear-filters-btn">
              <X className="h-3 w-3" />{pt ? 'Limpar' : 'Clear'}
            </button>
          )}
        </div>
      )}

      {/* Grupos por mês/ano */}
      {groups.map((g) => {
        const isCollapsed = !!collapsed[g.key];
        return (
          <div key={g.key} data-testid={`payroll-month-group-${g.key}`}>
            <button
              type="button"
              onClick={() => toggleGroup(g.key)}
              className="w-full flex items-center gap-2 py-1.5 text-left group"
              data-testid={`payroll-month-header-${g.key}`}
            >
              {isCollapsed ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{g.label}</span>
              <span className="text-[10px] text-slate-400">
                {g.items.length} {pt ? (g.items.length === 1 ? 'relatório' : 'relatórios') : (g.items.length === 1 ? 'report' : 'reports')} • {money(g.monthTotal)}
              </span>
              <span className="flex-1 border-t border-slate-200 group-hover:border-slate-300" />
            </button>
            {!isCollapsed && (
              <div className="space-y-2">
                {g.items.map((report) => {
                  const statusClass = STATUS_STYLES[(report.status || '').toLowerCase()] || 'bg-slate-100 text-slate-600';
                  return (
                    <div key={report.id} className="border rounded-lg bg-white p-3" data-testid={`payroll-report-card-${report.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-sm">
                              📅 {pt ? 'Semana até' : 'Week ending'} {fmtDate(report.week_ending)}
                            </span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusClass}`}>
                              {report.status || '—'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-1">
                            <span>👷 {report.entries?.length || 0} {pt ? 'trabalhos' : 'jobs'}</span>
                            <span>👥 {report._teams.length} {pt ? 'equipes' : 'teams'}</span>
                            <span className="font-medium text-green-700">💰 {money(report._laborTotal)}</span>
                            <span>{pt ? 'Criado' : 'Created'}: {fmtDateTime(report.created_at)}</span>
                          </div>
                          {search.trim() && report._matches.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 mt-2" data-testid={`payroll-report-matches-${report.id}`}>
                              <span className="text-[10px] font-semibold text-amber-700">
                                🔎 {report._matches.length} {pt ? 'resultado(s):' : 'match(es):'}
                              </span>
                              {report._matches.slice(0, 3).map((e, i) => (
                                <span key={i} className="text-[10px] bg-amber-50 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5 max-w-[180px] truncate">
                                  {e.team_name} • {e.customer}
                                </span>
                              ))}
                              {report._matches.length > 3 && (
                                <span className="text-[10px] text-amber-600">+{report._matches.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 flex-wrap shrink-0">
                          <Button size="sm" variant="outline" onClick={() => onView(report)} className="text-xs px-2 py-1 flex-1 sm:flex-none" data-testid={`payroll-view-report-${report.id}`}>
                            <Eye className="h-3 w-3 mr-1" /> {pt ? 'Ver Detalhes' : 'View Details'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={onGeneratePDFs} className="text-xs px-2 py-1 flex-1 sm:flex-none">
                            📄 PDFs
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {filtered.length === 0 && (
        <p className="text-center text-slate-500 py-8 text-sm" data-testid="payroll-reports-empty">
          {hasFilters
            ? (pt ? 'Nenhum relatório encontrado com esses filtros.' : 'No reports match these filters.')
            : (pt ? 'Nenhum relatório encontrado. Processe um arquivo Excel primeiro.' : 'No reports found. Process an Excel file first.')}
        </p>
      )}
    </div>
  );
};

export default PayrollReportsList;
